import serverCall from "../../serverCall";
import { getGlobalSocket, initGlobalSocket } from "../socket";
import { deductTokens, setTokenStatus, setActiveTranslation } from "../../store/slices/userSlice";

let activeTextTranslation = null;

const isTextTranslating = () => !!activeTextTranslation && !activeTextTranslation.resolved;
const getActiveTextTranslation = () => activeTextTranslation;

const translateTextViaSocket = (text, targetLanguage, onChunk, dispatch) => {
  // If an active socket translation for this exact text & target language is already running, reuse it!
  if (
    activeTextTranslation &&
    !activeTextTranslation.resolved &&
    activeTextTranslation.text === text &&
    activeTextTranslation.targetLanguage === targetLanguage
  ) {
    if (onChunk && !activeTextTranslation.listeners.includes(onChunk)) {
      activeTextTranslation.listeners.push(onChunk);
      if (activeTextTranslation.accumulated) {
        try {
          onChunk({
            type: "chunk",
            text: activeTextTranslation.accumulated,
            accumulated: activeTextTranslation.accumulated,
            personalIdentifiersProtected: activeTextTranslation.personalIdentifiersProtected,
          });
        } catch (_e) { }
      }
    }
    return activeTextTranslation.promise;
  }

  const listeners = onChunk ? [onChunk] : [];
  const notifyListeners = (event) => {
    listeners.forEach((fn) => {
      try {
        fn(event);
      } catch (_e) { }
    });
  };

  const promise = new Promise(async (resolve, reject) => {
    let sock = getGlobalSocket();
    if (!sock || !sock.connected) {
      sock = initGlobalSocket(dispatch);
    }

    if (!sock) {
      return translateTextStream(text, targetLanguage, notifyListeners).then(resolve).catch(reject);
    }

    let resolved = false;
    let accumulated = "";
    let paragraphs = {};
    let personalIdentifiersProtected = null;
    let timeoutTimer = null;

    const cleanup = () => {
      if (timeoutTimer) clearTimeout(timeoutTimer);
      if (activeTextTranslation && activeTextTranslation.text === text) {
        activeTextTranslation = null;
      }
      sock.off("connect", emitTranslate);
      sock.off("text-translation-response", handleTextTranslationResponse);
      sock.off("text-translation-error", handleError);
      sock.off("translate-text-result", handleResult);
      sock.off("translate-text-response", handleResult);
      sock.off("translate-response", handleResult);
      sock.off("translate-result", handleResult);
      sock.off("translation-result", handleResult);
      sock.off("translate-text-chunk", handleChunk);
      sock.off("translate-chunk", handleChunk);
      sock.off("translate-paragraph", handleParagraph);
      sock.off("translate-error", handleError);
      sock.off("translate-text-error", handleError);
      sock.off("translation-error", handleError);
      sock.off("error", handleError);
      sock.off("exception", handleError);
      sock.off("connect_error", handleConnectError);
    };

    const handleResult = (resultPayload) => {
      if (resolved) return;
      resolved = true;
      try {
        localStorage.removeItem("linko_pending_translation");
      } catch (_e) { }
      cleanup();

      const raw = resultPayload?.data !== undefined ? resultPayload.data : resultPayload;
      const finalTr = raw?.translation || raw?.translatedText || accumulated || "";
      const rawPi =
        raw?.personalIdentifiersProtected ??
        raw?.data?.personalIdentifiersProtected ??
        raw?.personal_identifiers_protected ??
        raw?.data?.personal_identifiers_protected ??
        resultPayload?.personalIdentifiersProtected ??
        resultPayload?.data?.personalIdentifiersProtected ??
        resultPayload?.personal_identifiers_protected ??
        resultPayload?.data?.personal_identifiers_protected ??
        personalIdentifiersProtected;
      const pi = rawPi !== null && rawPi !== undefined && !isNaN(Number(rawPi)) ? Number(rawPi) : (typeof personalIdentifiersProtected === "number" ? personalIdentifiersProtected : null);

      const tokenCost = raw?.totalToken || raw?.totalTokens || raw?.tokens;
      if (tokenCost && dispatch) {
        dispatch(deductTokens(tokenCost));
      }
      if ((raw?.tokenStatus || raw?.token_status) && dispatch) {
        dispatch(setTokenStatus(raw?.tokenStatus || raw?.token_status));
      }

      if (dispatch) {
        dispatch(
          setActiveTranslation({
            originalText: text,
            targetLanguage,
            translatedText: finalTr,
            personalIdentifiersProtected: pi,
            status: "completed",
            timestamp: Date.now(),
          })
        );
      }

      notifyListeners({
        type: "done",
        translation: finalTr,
        accumulated: finalTr,
        personalIdentifiersProtected: pi,
      });

      resolve({
        translation: finalTr,
        personalIdentifiersProtected: pi,
        raw,
      });
    };

    const handleError = (errPayload) => {
      if (resolved) return;
      resolved = true;
      try {
        localStorage.removeItem("linko_pending_translation");
        localStorage.removeItem("linko_active_translation");
      } catch (_e) { }
      cleanup();
      const raw = errPayload?.data !== undefined ? errPayload.data : errPayload;
      const msg =
        raw?.message ||
        raw?.error ||
        errPayload?.message ||
        errPayload?.error ||
        (typeof errPayload === "string" ? errPayload : "Please purchase a plan to access this feature.");
      const err = new Error(msg);
      err.response = { data: { message: msg } };
      err.partialResult = {
        translation: accumulated,
        personalIdentifiersProtected,
      };
      notifyListeners({
        type: "error",
        message: msg,
      });
      reject(err);
    };

    const handleConnectError = (err) => {
      console.warn("[Global Socket] Socket connect_error during translateTextViaSocket, falling back:", err);
      if (!resolved) {
        resolved = true;
        cleanup();
        translateTextStream(text, targetLanguage, notifyListeners).then(resolve).catch(reject);
      }
    };

    const handleChunk = (chunkPayload) => {
      const raw = chunkPayload?.data !== undefined ? chunkPayload.data : chunkPayload;
      if (raw?.status === "error" || raw?.status === false || (typeof raw?.statusCode === "number" && raw.statusCode >= 400)) {
        handleError(raw);
        return;
      }
      const textChunk = raw?.text || raw?.chunk || raw?.translation || "";
      accumulated += textChunk;
      if (raw?.personalIdentifiersProtected !== undefined && raw?.personalIdentifiersProtected !== null) {
        personalIdentifiersProtected = raw.personalIdentifiersProtected;
      }
      if (activeTextTranslation && activeTextTranslation.text === text) {
        activeTextTranslation.accumulated = accumulated;
        activeTextTranslation.personalIdentifiersProtected = personalIdentifiersProtected;
      }
      notifyListeners({
        type: "chunk",
        text: textChunk,
        accumulated,
        personalIdentifiersProtected,
      });
    };

    const handleParagraph = (paraPayload) => {
      const raw = paraPayload?.data !== undefined ? paraPayload.data : paraPayload;
      if (raw?.status === "error" || raw?.status === false || (typeof raw?.statusCode === "number" && raw.statusCode >= 400)) {
        handleError(raw);
        return;
      }
      const p = raw?.paragraph || raw;
      if (p && p.translation) {
        const idx = p.index !== undefined ? p.index : Object.keys(paragraphs).length;
        paragraphs[idx] = p.translation;
        accumulated = Object.keys(paragraphs)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => paragraphs[k])
          .join("\n\n");
        if (p.personalIdentifiersProtected !== undefined && p.personalIdentifiersProtected !== null) {
          personalIdentifiersProtected = p.personalIdentifiersProtected;
        }
        if (activeTextTranslation && activeTextTranslation.text === text) {
          activeTextTranslation.accumulated = accumulated;
          activeTextTranslation.personalIdentifiersProtected = personalIdentifiersProtected;
          activeTextTranslation.paragraphs = paragraphs;
        }
        if (p.totalToken && dispatch) {
          dispatch(deductTokens(p.totalToken));
        }
        notifyListeners({
          type: "paragraph",
          index: idx,
          translation: p.translation,
          originalText: p.originalText || "",
          personalIdentifiersProtected,
        });
      }
    };

    const handleTextTranslationResponse = (payload) => {
      // console.log("[Global Socket] 'text-translation-response' received:", payload);
      const raw = payload?.data !== undefined ? payload.data : payload;

      // 0. Error check
      if (
        raw?.status === "error" ||
        raw?.status === "failed" ||
        raw?.status === false ||
        (typeof raw?.statusCode === "number" && raw.statusCode >= 400) ||
        (raw?.error && !raw?.translation && !raw?.paragraph)
      ) {
        handleError(raw);
        return;
      }

      const p = raw?.paragraph;
      const paraEntry = Array.isArray(raw?.paragraphs)
        ? raw.paragraphs.find((it) => it && (it.personalIdentifiersProtected !== undefined || it.personal_identifiers_protected !== undefined)) || null
        : null;
      const rawPi =
        raw?.personalIdentifiersProtected ??
        raw?.data?.personalIdentifiersProtected ??
        raw?.personal_identifiers_protected ??
        raw?.data?.personal_identifiers_protected ??
        payload?.personalIdentifiersProtected ??
        payload?.data?.personalIdentifiersProtected ??
        payload?.personal_identifiers_protected ??
        p?.personalIdentifiersProtected ??
        p?.personal_identifiers_protected ??
        paraEntry?.personalIdentifiersProtected ??
        paraEntry?.personal_identifiers_protected ??
        null;
      if (rawPi !== null && rawPi !== undefined && !isNaN(Number(rawPi))) {
        personalIdentifiersProtected = Number(rawPi);
      }

      // 1. Paragraph / streaming chunk handling (capture any translated content first)
      if (p && p.translation) {
        const idx = p.index !== undefined ? p.index : Object.keys(paragraphs).length;
        paragraphs[idx] = p.translation;
        accumulated = Object.keys(paragraphs)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => paragraphs[k])
          .join("\n\n");

        if (activeTextTranslation && activeTextTranslation.text === text) {
          activeTextTranslation.accumulated = accumulated;
          activeTextTranslation.personalIdentifiersProtected = personalIdentifiersProtected;
          activeTextTranslation.paragraphs = paragraphs;
        }
        if (p.totalToken && dispatch) {
          dispatch(deductTokens(p.totalToken));
        }

        notifyListeners({
          type: "paragraph",
          index: idx,
          translation: p.translation,
          originalText: p.originalText || "",
          personalIdentifiersProtected,
        });

        if (dispatch) {
          dispatch(
            setActiveTranslation({
              originalText: text,
              targetLanguage,
              translatedText: accumulated,
              personalIdentifiersProtected,
              status: "processing",
              timestamp: Date.now(),
            })
          );
        }
      } else if (raw?.translation) {
        accumulated = raw.translation;
        if (activeTextTranslation && activeTextTranslation.text === text) {
          activeTextTranslation.accumulated = accumulated;
          activeTextTranslation.personalIdentifiersProtected = personalIdentifiersProtected;
        }
        notifyListeners({
          type: "chunk",
          text: raw.translation,
          accumulated,
          personalIdentifiersProtected,
        });

        if (dispatch) {
          dispatch(
            setActiveTranslation({
              originalText: text,
              targetLanguage,
              translatedText: accumulated,
              personalIdentifiersProtected,
              status: "processing",
              timestamp: Date.now(),
            })
          );
        }
      }

      // 2. Token updates
      const tokenCost = raw?.totalToken || raw?.totalTokens || raw?.tokens;
      if (tokenCost && dispatch) {
        dispatch(deductTokens(tokenCost));
      }
      if ((raw?.tokenStatus || raw?.token_status) && dispatch) {
        dispatch(setTokenStatus(raw?.tokenStatus || raw?.token_status));
      }

      // 3. Error check (after capturing any content)
      if (
        raw?.status === "error" ||
        raw?.status === false ||
        (typeof raw?.statusCode === "number" && raw.statusCode >= 400) ||
        (raw?.error && !raw?.translation && !raw?.paragraph)
      ) {
        handleError(raw);
        return;
      }

      // 4. Completion check
      const isCompleted =
        raw?.status === "completed" ||
        raw?.responseType === "final" ||
        raw?.responseType === "done" ||
        (typeof raw?.completed === "number" && typeof raw?.total === "number" && raw.completed >= raw.total);

      if (isCompleted) {
        const finalTr = accumulated || raw?.translation || raw?.translatedText || "";
        handleResult({
          translation: finalTr,
          personalIdentifiersProtected,
          totalToken: tokenCost,
        });
      }
    };

    sock.on("text-translation-response", handleTextTranslationResponse);
    sock.on("text-translation-error", handleError);
    sock.on("translate-text-result", handleResult);
    sock.on("translate-text-response", handleResult);
    sock.on("translate-response", handleResult);
    sock.on("translate-result", handleResult);
    sock.on("translation-result", handleResult);
    sock.on("translate-text-chunk", handleChunk);
    sock.on("translate-chunk", handleChunk);
    sock.on("translate-paragraph", handleParagraph);
    sock.on("translate-error", handleError);
    sock.on("translate-text-error", handleError);
    sock.on("translation-error", handleError);
    sock.on("error", handleError);
    sock.on("exception", handleError);
    sock.on("connect_error", handleConnectError);

    const handleSameEvent = (payload) => {
      if (payload && (payload.translation || payload.translatedText || payload.text || payload.statusCode === 200 || payload.data)) {
        handleResult(payload);
      }
    };
    sock.once("translate-text", handleSameEvent);

    const emitTranslate = () => {
      // console.log("[Global Socket] Emitting 'translate-text' event:", { text, targetLanguage });
      sock.emit("translate-text", { text, targetLanguage }, (ackResponse) => {
        if (ackResponse) {
          // console.log("[Global Socket] 'translate-text' ack received:", ackResponse);
          if (
            ackResponse.status === "error" ||
            ackResponse.status === "failed" ||
            ackResponse.status === false ||
            (typeof ackResponse.statusCode === "number" && ackResponse.statusCode >= 400) ||
            ackResponse.error
          ) {
            handleError(ackResponse);
          } else {
            handleResult(ackResponse);
          }
        }
      });
    };

    if (sock.connected) {
      emitTranslate();
    } else {
      sock.once("connect", emitTranslate);
    }

    timeoutTimer = setTimeout(() => {
      if (!resolved) {
        console.warn("[Global Socket] Socket 'translate-text' timeout, falling back to HTTP stream...");
        cleanup();
        translateTextStream(text, targetLanguage, notifyListeners).then(resolve).catch(reject);
      }
    }, 25000);
  });

  activeTextTranslation = {
    text,
    targetLanguage,
    listeners,
    accumulated: "",
    paragraphs: {},
    personalIdentifiersProtected: null,
    promise,
    resolved: false,
  };

  return promise;
};

const translateText = (text, targetLanguage) => {
  try {
    const response = serverCall.post(`/ai-conversation/translate-text`, {
      text,
      targetLanguage,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const translateTextStream = (text, targetLanguage, onChunk) => {
  return new Promise(async (resolve, reject) => {
    try {
      const token = localStorage.getItem("linko_web");
      const baseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";

      const response = await fetch(`${baseURL}/ai-conversation/translate-text-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text, targetLanguage }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return reject(new Error(errData?.message || `Request failed: ${response.status}`));
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data?.status === false || data?.error || (data?.success === false && data?.message)) {
          return reject(new Error(data?.message || data?.error || "AI translation failed"));
        }
        const d = data?.data || data;
        const translation = d?.translation || d?.translatedText || d?.text || "";
        const personalIdentifiersProtected = d?.personalIdentifiersProtected ?? (typeof data?.personalIdentifiersProtected === "number" ? data.personalIdentifiersProtected : null);
        if (onChunk) onChunk({ type: "done", translation, personalIdentifiersProtected });
        return resolve({ translation, personalIdentifiersProtected });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "";
      let result = { translation: "", personalIdentifiersProtected: null, paragraphs: {} };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.replace("event:", "").trim();
          } else if (line.startsWith("data:")) {
            const raw = line.replace("data:", "").trim();
            try {
              const parsed = JSON.parse(raw);
              if (currentEvent === "error" || parsed?.error || (parsed?.status === false && parsed?.message)) {
                const errMsg = parsed?.message || parsed?.error || "AI translation failed";
                const err = new Error(errMsg);
                err.partialResult = result;
                return reject(err);
              }
              if (parsed?.personalIdentifiersProtected !== undefined && parsed?.personalIdentifiersProtected !== null) {
                result.personalIdentifiersProtected = parsed.personalIdentifiersProtected;
              }
              if (currentEvent === "paragraph") {
                if (parsed?.translation) {
                  if (parsed.index !== undefined && parsed.index !== null) {
                    result.paragraphs[parsed.index] = parsed.translation;
                    result.translation = Object.keys(result.paragraphs)
                      .sort((a, b) => Number(a) - Number(b))
                      .map((k) => result.paragraphs[k])
                      .join("\n\n");
                  } else {
                    result.translation = [result.translation, parsed.translation].filter(Boolean).join("\n\n");
                  }
                  if (onChunk) onChunk({ type: "paragraph", index: parsed.index, translation: parsed.translation, originalText: parsed.originalText || "", personalIdentifiersProtected: result.personalIdentifiersProtected });
                }
              } else if (currentEvent === "chunk") {
                if (parsed?.text) {
                  result.translation += parsed.text;
                  if (onChunk) onChunk({ type: "chunk", text: parsed.text, accumulated: result.translation, personalIdentifiersProtected: result.personalIdentifiersProtected });
                }
              } else if (currentEvent === "done") {
                result.translation = parsed?.text || parsed?.translation || parsed?.translatedText || result.translation;
                if (onChunk) onChunk({ type: "done", translation: result.translation, personalIdentifiersProtected: result.personalIdentifiersProtected });
              } else {
                if (parsed?.translation) {
                  if (parsed.index !== undefined && parsed.index !== null) {
                    result.paragraphs[parsed.index] = parsed.translation;
                    result.translation = Object.keys(result.paragraphs)
                      .sort((a, b) => Number(a) - Number(b))
                      .map((k) => result.paragraphs[k])
                      .join("\n\n");
                  } else {
                    result.translation = [result.translation, parsed.translation].filter(Boolean).join("\n\n");
                  }
                  if (onChunk) onChunk({ type: "paragraph", index: parsed.index, translation: parsed.translation, originalText: parsed.originalText || "", personalIdentifiersProtected: result.personalIdentifiersProtected });
                } else if (parsed?.text) {
                  result.translation += parsed.text;
                  if (onChunk) onChunk({ type: "chunk", text: parsed.text, accumulated: result.translation, personalIdentifiersProtected: result.personalIdentifiersProtected });
                }
              }
            } catch { }
          }
        }
      }

      resolve(result);
    } catch (err) {
      reject(err);
    }
  });
};

const translateDocument = (file, targetLanguage) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("targetLanguage", targetLanguage);
    const response = serverCall.post(`/ai-conversation/translate-document`, formData);
    return response;
  } catch (error) {
    throw error;
  }
};

const extractDocumentText = (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = serverCall.post(`/ai-conversation/extract-document-text`, formData);
    return response;
  } catch (error) {
    throw error;
  }
};

const retranslate = (id, type, text, targetLanguage) => {
  return serverCall.patch(`/ai-conversation/translation/${id}/retranslate`, {
    type,
    text,
    targetLanguage,
  });
};

let activeDocExtraction = null;

const setDocExtracting = (status, fileName = "") => {
  if (status) {
    activeDocExtraction = { isExtracting: true, fileName };
  } else {
    activeDocExtraction = null;
  }
};

const isDocExtracting = () => {
  return !!activeDocExtraction?.isExtracting;
};

const TranslateService = {
  translateText,
  translateTextStream,
  translateTextViaSocket,
  translateDocument,
  extractDocumentText,
  retranslate,
  setDocExtracting,
  isDocExtracting,
  isTextTranslating,
  getActiveTextTranslation,
};

export default TranslateService;
